import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'state/store';

export interface InsightNote {
  id: string | number;
  text?: string;
  xPct?: number;
  yPct?: number;
  [key: string]: unknown;
}

export interface InsightApiNote {
  message: string;
  positionXRelative: number;
  positionYRelative: number;
}

export interface InsightState {
  imageUrl: string | null;
  imageBlob: Blob | null;
  notes: InsightNote[];
  step: number;
  metadata: Record<string, unknown>;
  assignees: unknown[];
  autoAssignToUsers: boolean;
  allUsers: unknown[];
  relatedGoalIds: Array<string | number>;
}

const initialState: InsightState = {
  imageUrl: null,
  imageBlob: null,
  notes: [],
  step: 1,
  metadata: {},
  assignees: [],
  autoAssignToUsers: false,
  allUsers: [],
  relatedGoalIds: [],
};

const revokeBlobUrlIfNeeded = (url: string | null): void => {
  if (typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

interface InitWithImagePayload {
  blob?: Blob | null;
  url?: string | null;
}

const insightSlice = createSlice({
  name: 'insight',
  initialState,
  reducers: {
    reset(state) {
      revokeBlobUrlIfNeeded(state.imageUrl);
      state.imageUrl = null;
      state.imageBlob = null;
      state.notes = [];
      state.step = 1;
      state.metadata = {};
      state.assignees = [];
      state.autoAssignToUsers = false;
      state.allUsers = [];
      state.relatedGoalIds = [];
    },
    initWithImage(state, action: PayloadAction<InitWithImagePayload | undefined>) {
      const { blob, url } = action.payload || {};
      const nextUrl = url || null;

      if (state.imageUrl && state.imageUrl !== nextUrl) {
        revokeBlobUrlIfNeeded(state.imageUrl);
      }

      state.imageBlob = blob || null;
      state.imageUrl = nextUrl;
      state.notes = [];
      state.step = 1;
    },
    setNotes(state, action: PayloadAction<InsightNote[] | undefined>) {
      state.notes = Array.isArray(action.payload) ? action.payload : [];
    },
    addNote(state, action: PayloadAction<InsightNote>) {
      state.notes.push(action.payload);
    },
    updateNote(
      state,
      action: PayloadAction<{ id: string | number; text: string }>,
    ) {
      const { id, text } = action.payload;
      const idx = state.notes.findIndex((n) => n.id === id);

      if (idx !== -1) state.notes[idx].text = text;
    },
    deleteNote(state, action: PayloadAction<string | number>) {
      const id = action.payload;

      state.notes = state.notes.filter((n) => n.id !== id);
    },
    setStep(state, action: PayloadAction<number>) {
      state.step = action.payload;
    },
    setMetadata(state, action: PayloadAction<Record<string, unknown> | undefined>) {
      state.metadata = { ...state.metadata, ...(action.payload || {}) };
    },
    setAssignees(state, action: PayloadAction<unknown[] | undefined>) {
      state.assignees = Array.isArray(action.payload) ? action.payload : [];
    },
    setAutoAssignToUsers(state, action: PayloadAction<boolean | unknown>) {
      state.autoAssignToUsers = Boolean(action.payload);
    },
    setAllUsers(state, action: PayloadAction<unknown[] | undefined>) {
      state.allUsers = Array.isArray(action.payload) ? action.payload : [];
    },
    setRelatedGoalIds(
      state,
      action: PayloadAction<Array<string | number> | undefined>,
    ) {
      state.relatedGoalIds = Array.isArray(action.payload)
        ? action.payload
        : [];
    },
  },
});

export const {
  reset,
  initWithImage,
  setNotes,
  addNote,
  updateNote,
  deleteNote,
  setStep,
  setMetadata,
  setAssignees,
  setAutoAssignToUsers,
  setAllUsers,
  setRelatedGoalIds,
} = insightSlice.actions;

export default insightSlice.reducer;

export const mapNotesToApi = (notes: InsightNote[] | unknown): InsightApiNote[] =>
  (Array.isArray(notes) ? (notes as InsightNote[]) : []).map((n) => ({
    message: n.text || '',
    positionXRelative: Number((n.xPct ?? 0).toFixed(4)),
    positionYRelative: Number((n.yPct ?? 0).toFixed(4)),
  }));

export const selectInsightApiNotes: (state: RootState) => InsightApiNote[] =
  createSelector(
    [(state: RootState) => state?.insight?.notes || []],
    (notes) => mapNotesToApi(notes),
  );
